import React from 'react';
import clsx from 'clsx';

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'text' | 'rect' | 'circle' | 'avatar' | 'card';
  lines?: number;
  animate?: boolean;
  width?: number | string;
  height?: number | string;
  lineClassName?: string;
}

const lineWidths = ['100%', '92%', '78%', '66%'];

export const Skeleton: React.FC<SkeletonProps> = ({
  variant = 'text',
  lines = 1,
  animate = true,
  width,
  height,
  className,
  lineClassName,
  style,
  ...props
}) => {
  const isText = variant === 'text';
  const sharedClasses = clsx(
    'relative overflow-hidden bg-slate-200/90 border border-black/10',
    '[.theme-liquid-glass_&]:bg-white/10 [.theme-liquid-glass_&]:border-white/10',
    animate && 'animate-pulse'
  );

  const variantClasses = {
    text: 'h-4 rounded-md border-transparent',
    rect: 'rounded-xl min-h-[6rem]',
    circle: 'rounded-full min-h-[3rem] min-w-[3rem]',
    avatar: 'rounded-full min-h-[3.5rem] min-w-[3.5rem]',
    card: 'rounded-2xl min-h-[10rem]',
  } as const;

  const resolvedStyle = {
    width,
    height,
    ...style,
  };

  if (isText && lines > 1) {
    return (
      <div
        aria-hidden="true"
        data-slot="skeleton"
        className={clsx('flex flex-col gap-2', className)}
        {...props}
      >
        {Array.from({ length: lines }, (_, index) => (
          <div
            key={index}
            data-slot="skeleton-line"
            className={clsx(sharedClasses, variantClasses.text, lineClassName)}
            style={{
              width: index === lines - 1 ? lineWidths[index % lineWidths.length] : '100%',
            }}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      aria-hidden="true"
      data-slot="skeleton"
      className={clsx(sharedClasses, variantClasses[variant], className)}
      style={resolvedStyle}
      {...props}
    />
  );
};
